import { Fragment, useEffect, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { Heart, X } from 'lucide-react'
import { Card, useToast } from '../ui'
import {
  heroAlt, heroCredit, heroFallbackUrl, heroIsVenue, heroSrcSet, heroUrl, posterUrl, signalFeatures, idsOf, swatch,
} from '../lib/entries'
import { FORM_LABEL, classicalForm } from '../lib/classicalForm'
import { decideLine, kindLine, rowTitle, whatLine, whereLine } from '../lib/rowMeta'
import { useEntryState } from '../lib/queries'
import { useDismissEntry, useForgetEntry, useSaveEntry } from '../lib/mutations'
import type { FeedRow } from '../lib/types'
import type { ShowingLite } from './CardBody'

/**
 * The picks (H2, WP 5.3; the card itself WP 6.2): a rail of cards you swipe
 * through.
 *
 * Doug, 26 Sept: "The pick for tonight isn't totally clear. It would be even
 * better if that was swipeable, like if I could swipe through the stuff for
 * tonight." So the one lead card is up to five, side by side, and which five
 * and what the rail is called are `today.ts`'s (`pickLeads`, `picksWindow`)
 * where `npm test` can see them.
 *
 * **The prototype's `.pick` card, ported.** WP 6.1 gave every other list in
 * the app `kindLine`/`decideLine`/`whereLine`/`whatLine` and left this file on
 * the older `metaLine` on purpose — its own header names `EventRow` as the
 * only caller that moved. This package is the second: the same four functions,
 * the same lines in the same order, and (new here) the prototype's three
 * buttons — Save, Not for me, More — so the picture, the five lines and the
 * actions are all one card rather than a card that opens a row's worth of
 * controls only once you tap through.
 *
 * **No sentence about why.** H2's constraint is unchanged and is the whole
 * specification of what a card says: "I like the idea of the lead card, but I
 * don't want to have an AI rationale. Just give me what the thing is." Nothing
 * from `score_reasons` reaches this file, or any other file on this screen.
 *
 * **The label is the tag, once.** The old card wore "Pick for tonight" on
 * itself, which meant the screen said "tonight" twice in two lines the moment
 * there was a heading over it. The rail's heading carries the window and the
 * cards carry events.
 */
export function Picks({ picks, label, showings, onOpen }: {
  picks: FeedRow[]
  /** `picksWindow(now).label`: "Tonight", "Today", or a weekday name. One of
   *  three fixed shapes, never a description of what is in the rail. */
  label: string
  /** Every pick's showings, by entry id — the same map the sections read. */
  showings?: Map<string, ShowingLite[]>
  onOpen: (e: FeedRow) => void
}) {
  const rail = useRef<HTMLDivElement>(null)
  const [active, setActive] = useState(0)

  /**
   * Which card the rail is showing, from an `IntersectionObserver` rather than
   * from `scrollLeft`.
   *
   * Scroll maths would need the card width, the gap and the scroll padding —
   * three numbers that live in `app.css` and would have to be right here too,
   * which is four places for one layout to be wrong in. The observer asks the
   * browser what is actually on screen. A threshold of 0.6 is unambiguous
   * with a 24px peek: one card is over it at a time, and the peeking neighbour
   * is at 0.07.
   */
  useEffect(() => {
    const root = rail.current
    if (!root || picks.length < 2) { setActive(0); return }
    // Read off the DOM rather than through a ref per card: `ui/Card` takes one
    // ref and this component is not the place to start threading a second one
    // through it. The rail owns its children and `.pickcard` is its own class.
    const els = [...root.querySelectorAll<HTMLElement>('.pickcard')]
    const io = new IntersectionObserver(entries => {
      for (const en of entries) {
        if (en.intersectionRatio < 0.6) continue
        const i = els.indexOf(en.target as HTMLElement)
        if (i >= 0) setActive(i)
      }
    }, { root, threshold: [0.6] })
    for (const el of els) io.observe(el)
    return () => io.disconnect()
  }, [picks])

  // Nothing above the bar is no rail and no label, exactly as the lead card
  // behaved: a heading over an empty scroller is a promise the data cannot
  // keep, and `Empty` below the sections already says the screen is quiet.
  if (!picks.length) return null

  return (
    <section className="picks">
      <h2 className="picks__label">{label}</h2>
      <div
        ref={rail}
        className={`picks__rail${picks.length === 1 ? ' picks__rail--one' : ''}`}
        role="region"
        aria-label={`${label} picks`}
        aria-roledescription="carousel"
        /* A scrollable box has to be reachable by keyboard, or the second card
           is only ever available to a finger. `role="region"` gives the stop a
           name when it is focused. */
        tabIndex={0}
      >
        {picks.map(e => (
          <PickCard
            key={e.id}
            e={e}
            showings={showings?.get(e.id)}
            onOpen={() => onOpen(e)}
          />
        ))}
      </div>
      {/* One dot per card, the current one in `--ink`. `aria-hidden`, because
          it is a picture of the scroll position and the rail itself is what a
          screen reader moves through. */}
      {picks.length > 1 && (
        <div className="picks__dots" aria-hidden="true">
          {/* Clamped rather than trusted: a shorter list arriving while the
              rail was on its last card would leave `active` past the end and
              every dot off, which reads as a broken control for the frame it
              takes the observer to fire again. */}
          {picks.map((e, i) => (
            <span
              key={e.id}
              className={`picks__dot${i === Math.min(active, picks.length - 1) ? ' is-on' : ''}`}
            />
          ))}
        </div>
      )}
    </section>
  )
}

/**
 * One pick.
 *
 * The picture is the event's own or there is none. A venue photograph is a
 * picture of the Philharmonie standing in for a picture of the concert, and D7
 * already keeps it off every row; across a card it would be the largest thing
 * on the front page and still not be a picture of the thing.
 *
 * Below it, the same four lines `EventRow` draws — kind, title, what it is,
 * the deciding line, where — and then the three buttons: Save, Not for me,
 * More. **It owns the writes**, the same arrangement `EventRow.tsx`'s own
 * header describes: one `entry_state` query, three shared mutations, so a
 * pick saved from the rail and the same row saved from Browse cannot
 * disagree about whether it is saved.
 */
function PickCard({ e, showings, onOpen }: {
  e: FeedRow
  showings?: ShowingLite[]
  onOpen: () => void
}) {
  const { data: state } = useEntryState()
  const saved = (state ?? []).some(r => r.entry_id === e.id && r.state === 'saved')

  const saveM = useSaveEntry()
  const forgetM = useForgetEntry()
  const dismissM = useDismissEntry()
  const toast = useToast()

  const ids = idsOf(e)
  const save = () => saveM.mutate({ ids, features: signalFeatures(e) })
  const forget = () => forgetM.mutate({ ids })

  /** F7's own rule, restated for the rail: removal is an action and a toast,
   *  never a confirmation dialog, and the undo is a delete rather than a
   *  third state. */
  const dismiss = () => {
    dismissM.mutate({ ids, features: signalFeatures(e) })
    toast({ text: 'Hidden', action: { label: 'Undo', onClick: forget } })
  }

  // The three stages `Hero` uses, and the hook is above any early return for
  // the reason its own comment gives: a hook below one does not run on the
  // first render.
  const [stage, setStage] = useState<0 | 1 | 2>(0)
  /** Whether the stage's picture has arrived. Until it has, the `<img>` is
   *  transparent and the swatch under it is what shows (UI audit a1-35): a
   *  sized image still loading is drawn by Chrome as its alt text beside a
   *  broken-picture icon, which on a slow cold load read as "Konzerthaus
   *  Berlin, Großer Saal" printed over the gradient. The alt stays on the
   *  element for a screen reader; only its painting waits. An error moves to
   *  the next stage and, at the last, removes the `<img>` — the swatch alone. */
  const [loaded, setLoaded] = useState(false)
  const media = useRef<HTMLButtonElement>(null)

  // A poster before anything else, and `heroIsVenue` asked only where there is
  // no poster to prefer — `FilmPoster` has the same guard and the same comment:
  // asking it first throws away the poster of every film at a cinema that has
  // a photograph, which is most of them.
  const poster = posterUrl(e, 'w500')
  const own = poster ?? (heroIsVenue(e) ? null : heroUrl(e, 800))
  const src = own === null ? null : stage === 0 ? own : stage === 1 ? heroFallbackUrl(e) : null
  const credit = own && src ? heroCredit(e) : null

  /**
   * Open the sheet, carrying the picture across.
   *
   * `EventRow.open`'s dance, and its comment is the one to read: the name is
   * set on this card's picture for the length of the transition only, because
   * two elements holding one `view-transition-name` at the same moment is
   * invalid and the browser abandons the whole transition — so the card claims
   * it, hands it to the sheet's hero, and drops it again *inside* the update
   * callback, where exactly one element holds it in each snapshot.
   * `flushSync` is what makes that callback actually change the DOM.
   *
   * Written out rather than shared with `EventRow`: this package's brief lists
   * the files it may change and `EventRow.tsx` is not one of them. It is the
   * one thing in here worth lifting into a helper when a third caller wants
   * it.
   */
  function open() {
    const el = media.current
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    const doc = document as ViewTransitionDocument
    if (!el || reduced || !doc.startViewTransition) { onOpen(); return }
    el.style.viewTransitionName = `hero-${e.id}`
    const t = doc.startViewTransition(() => {
      flushSync(() => onOpen())
      el.style.viewTransitionName = ''
    })
    void t.finished.catch(() => {}).finally(() => { el.style.viewTransitionName = '' })
  }

  const now = new Date()
  const form = classicalForm(e)
  const k = kindLine(e, { now, showings, form: form ? FORM_LABEL[form] : null })
  const what = whatLine(e, { showings })
  const decide = decideLine(e)
  const where = whereLine(e, { now, showings })

  return (
    /* `--text` when there is no picture at all: the card is then centred
       typography rather than a tall box with its content at the top. See
       `app.css`. */
    <Card className={own ? 'pickcard' : 'pickcard pickcard--text'}>
      {own && (
        <button
          ref={media}
          className={`pickcard__media${poster ? ' pickcard__media--poster' : ''}`}
          onClick={open}
          aria-label={rowTitle(e)}
        >
          <span className="ph" style={{ background: poster ? undefined : swatch(e.id) }} />
          {src && (
            <img
              key={stage}
              className={loaded ? 'ph' : 'ph is-pending'} src={src}
              srcSet={stage === 0 ? (heroSrcSet(e) ?? undefined) : undefined}
              sizes="(max-width: 680px) calc(100vw - 56px), 624px"
              width={624} height={416}
              alt={heroAlt(e)} decoding="async"
              onLoad={() => setLoaded(true)}
              onError={() => { setLoaded(false); setStage(s => (s === 0 ? 1 : 2)) }}
            />
          )}
        </button>
      )}
      <button className="pickcard__tap" onClick={open}>
        {/* The kind line: the dot, the word, the genre or form, and when —
            `EventRow`'s own anatomy, at the card's own size. */}
        <p className="pickcard__meta">
          <span className="pickcard__dot" style={{ background: k.dot }} aria-hidden="true" />
          <b className="pickcard__kind">{k.kind}</b>
          {k.genre && <span className="pickcard__genre">{k.genre}</span>}
          {k.when && <b className="pickcard__when">{k.when}</b>}
        </p>
        <p className="pickcard__title">{rowTitle(e)}</p>
        {what && <p className="pickcard__what">{what}</p>}
        {decide.length > 0 && (
          <p className="pickcard__decide">
            {decide.map((part, i) => (
              <Fragment key={i}>
                {i > 0 && <span className="pickcard__sep" aria-hidden="true">{' · '}</span>}
                {part.strong ? <b>{part.text}</b> : part.text}
              </Fragment>
            ))}
          </p>
        )}
        {/* Rule 3: nothing truncates the venue — the same reason `EventRow`'s
            own where line wraps rather than ellipsising. */}
        <p className="pickcard__where">
          {/* Only when there is one: a citywide row (berlin.de events) has no
              place yet, and an empty <b> left the dot before "once" standing
              alone — `.pickcard__wfact:first-child` drops it, as EventRow does. */}
          {where.venue && <b className="pickcard__venue">{where.venue}</b>}
          {where.more > 0 && <span className="pickcard__more">{` +${where.more}`}</span>}
          {where.parts.length > 0 && <span className="pickcard__wfact">{where.parts.join(' · ')}</span>}
        </p>
      </button>
      {/* Attribution is a condition of showing a picture, never optional —
          see `heroCredit` and the head of `src/harvest/venuehero.ts`. */}
      {credit && <p className="imgcredit pickcard__credit">{credit}</p>}
      {/* The prototype's three buttons: Save, Not for me, More. "More" opens
          the same sheet the picture and the text already do — a fourth way to
          the same place, for the one gesture (tap the row) this card has no
          swipe for. */}
      <div className="pickcard__btns">
        <button
          type="button"
          className={`pickcard__btn${saved ? ' is-on' : ''}`}
          aria-pressed={saved}
          onClick={() => (saved ? forget() : save())}
        >
          <Heart size={15} fill={saved ? 'currentColor' : 'none'} />
          {saved ? 'Saved' : 'Save'}
        </button>
        <button type="button" className="pickcard__btn" onClick={dismiss}>
          <X size={15} />
          Not for me
        </button>
        <button
          type="button" className="pickcard__btn pickcard__btn--more" onClick={open}
          aria-label={`More about ${rowTitle(e)}`}
        >
          ···
        </button>
      </div>
    </Card>
  )
}

/** `startViewTransition` is not in TypeScript's DOM lib in every version this
 *  repo builds against — `EventRow.tsx` declares the same shape for the same
 *  reason, and neither file may import the other's. */
type ViewTransitionDocument = Document & {
  startViewTransition?: (cb: () => void) => { finished: Promise<void> }
}
